import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import {
  MM_BRAND,
  MM_CATEGORIES,
  MM_ROOT,
  mmPath,
} from "@/data/medicalMobility";

/* eslint-disable no-restricted-syntax -- approved owner artwork, served as pre-generated WebP */

// Pre-generated static derivatives: no runtime transform, so the first visitor is not charged for
// the resize. Widths must match backend/generate_medical_mobility_assets.py.
export const MmImage = ({
  base,
  alt,
  sizes,
  priority = false,
  className,
  widths = [480, 768, 1024, 1400],
  width = 1448,
  height = 1086,
}) => (
  <img
    src={`${base}-${widths[widths.length - 1]}.webp`}
    srcSet={widths.map((w) => `${base}-${w}.webp ${w}w`).join(", ")}
    sizes={sizes}
    alt={alt}
    width={width}
    height={height}
    loading={priority ? "eager" : "lazy"}
    decoding="async"
    {...(priority ? { fetchPriority: "high" } : {})}
    className={className}
  />
);

const LOGO = "/media/medical-mobility/logo-medical-mobility";

export const MedicalLogo = ({ className = "h-[50px] sm:h-[56px] md:h-[66px] lg:h-[74px]" }) => {
  const { pathname, hash } = useLocation();
  // Clicking the logo on the hub itself does not re-run the router's scroll reset, so handle it.
  const onClick = (e) => {
    if (pathname !== MM_ROOT || hash) return;
    e.preventDefault();
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  };
  return (
    <Link
      to={MM_ROOT}
      onClick={onClick}
      data-testid="mm-logo"
      aria-label="Human Home Living Medical & Mobility home"
      className="inline-flex items-center shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sage"
    >
      <img
        src={`${LOGO}-400.webp`}
        srcSet={`${LOGO}-200.webp 200w, ${LOGO}-400.webp 400w`}
        sizes="(min-width:768px) 160px, 120px"
        width={1305}
        height={855}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        className={`${className} w-auto select-none`}
        style={{ aspectRatio: "1305 / 855" }}
      />
    </Link>
  );
};

// Swaps the browser icon to the Medical & Mobility mark while a visitor is inside the section, and
// restores the Human Home Living icon on the way out.
const MAIN_ICONS = { icon: "/favicon.ico?v=4", apple: "/hhl-icon-180.png?v=4" };
const MM_ICONS = {
  icon: "/media/medical-mobility/favicon-32x32.png",
  apple: "/media/medical-mobility/favicon-180x180.png",
};

// A link pointing at the page the visitor is already on does not re-run the router's scroll reset,
// so the section handles that case itself: jump to the top rather than appear to do nothing.
export const useSameRouteToTop = () => {
  const { pathname, hash } = useLocation();
  return (to) => (e) => {
    if (to !== pathname || hash) return;
    e.preventDefault();
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  };
};

export const useMedicalFavicon = () => {
  useEffect(() => {
    const apply = ({ icon, apple }) => {
      const set = (rel, href, type) => {
        let el = document.head.querySelector(`link[rel="${rel}"]`);
        if (!el) {
          el = document.createElement("link");
          el.setAttribute("rel", rel);
          document.head.appendChild(el);
        }
        if (type) el.setAttribute("type", type);
        el.setAttribute("href", href);
      };
      set("icon", icon, icon.startsWith("/media") ? "image/png" : "image/x-icon");
      set("apple-touch-icon", apple);
    };
    apply(MM_ICONS);
    return () => apply(MAIN_ICONS);
  }, []);
};

export const MedicalHeader = () => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const menuRef = useRef(null);
  const toTop = useSameRouteToTop();

  useEffect(() => {
    setOpen(false);
    setMenu(false);
  }, [pathname]);

  useEffect(() => {
    if (!menu) return;
    const close = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenu(false);
    };
    const esc = (e) => {
      if (e.key === "Escape") setMenu(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [menu]);

  const linkClass = ({ isActive }) =>
    `link-underline font-medium transition-colors ${
      isActive ? "text-sage" : "text-charcoal hover:text-sage"
    }`;

  return (
    <>
      <a
        href="#main-content"
        data-testid="mm-skip-to-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:left-4 focus:top-4 focus:bg-charcoal focus:text-white focus:px-5 focus:py-3 focus:text-[12.5px] focus:tracking-[0.14em] focus:uppercase"
      >
        Skip to content
      </a>
      <header
        data-testid="mm-header"
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-line"
      >
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 flex items-center justify-between h-[68px] md:h-[80px] lg:h-[88px]">
          <MedicalLogo />

          <nav aria-label="Medical and Mobility" className="hidden lg:flex items-center gap-7 text-[13.5px]">
            <NavLink to={MM_ROOT} end onClick={toTop(MM_ROOT)} data-testid="mm-nav-home" className={linkClass}>
              Medical &amp; Mobility Home
            </NavLink>
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenu((v) => !v)}
                aria-expanded={menu}
                aria-haspopup="true"
                data-testid="mm-nav-categories"
                className="inline-flex items-center gap-1.5 font-medium text-charcoal hover:text-sage transition-colors"
              >
                Equipment Categories
                <ChevronDown size={15} className={`transition-transform ${menu ? "rotate-180" : ""}`} />
              </button>
              {menu && (
                <div
                  data-testid="mm-nav-dropdown"
                  className="absolute right-0 top-full mt-3 w-[320px] bg-white border border-line shadow-lg py-2"
                >
                  {MM_CATEGORIES.map((c) => (
                    <NavLink
                      key={c.slug}
                      to={mmPath(c.slug)}
                      onClick={(e) => {
                        toTop(mmPath(c.slug))(e);
                        setMenu(false);
                      }}
                      data-testid={`mm-nav-${c.slug}`}
                      className={({ isActive }) =>
                        `block px-5 py-3 text-[14px] transition-colors ${
                          isActive ? "text-sage bg-sage-soft" : "text-charcoal hover:bg-bone"
                        }`
                      }
                    >
                      {c.nav}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to={`${MM_ROOT}#categories`}
              data-testid="mm-header-cta"
              className="hidden md:inline-flex items-center px-5 py-2.5 text-[12.5px] tracking-wide uppercase bg-charcoal text-white hover:bg-sage transition-colors duration-300"
            >
              Explore Equipment
            </Link>
            <button
              data-testid="mm-mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mm-mobile-nav"
              className="lg:hidden p-3 -mr-2 text-charcoal"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <nav
            id="mm-mobile-nav"
            aria-label="Medical and Mobility mobile"
            data-testid="mm-mobile-nav"
            className="lg:hidden border-t border-line bg-white px-5 py-3 max-h-[70vh] overflow-y-auto"
          >
            <Link
              to={MM_ROOT}
              onClick={(e) => {
                toTop(MM_ROOT)(e);
                setOpen(false);
              }}
              data-testid="mm-mobile-nav-home"
              className="block py-3.5 text-[15px] border-b border-line/70 font-medium"
            >
              Medical &amp; Mobility Home
            </Link>
            {MM_CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={mmPath(c.slug)}
                onClick={(e) => {
                  toTop(mmPath(c.slug))(e);
                  setOpen(false);
                }}
                data-testid={`mm-mobile-nav-${c.slug}`}
                className="block py-3.5 text-[15px] border-b border-line/70 font-light"
              >
                {c.nav}
              </Link>
            ))}
            <Link
              to="/contact?ref=medical-mobility"
              onClick={() => setOpen(false)}
              data-testid="mm-mobile-nav-contact"
              className="block py-3.5 text-[15px] font-light"
            >
              Contact
            </Link>
          </nav>
        )}
      </header>
    </>
  );
};

const FooterLink = ({ to, children, testId }) => {
  const toTop = useSameRouteToTop();
  return (
    <Link
      to={to}
      onClick={toTop(to)}
      data-testid={testId}
      className="block text-[14.5px] py-1.5 text-[#4F4B45] hover:text-sage-dark transition-colors"
    >
      {children}
    </Link>
  );
};

export const MedicalFooter = () => (
  <footer data-testid="mm-footer" className="bg-white border-t border-line mt-24">
    <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-20">
      <div className="grid md:grid-cols-[1.4fr_1fr_1fr] gap-10 md:gap-12">
        <div>
          <img
            src={`${LOGO}-400.webp`}
            srcSet={`${LOGO}-200.webp 200w, ${LOGO}-400.webp 400w`}
            sizes="200px"
            width={1305}
            height={855}
            alt="Human Home Living Medical & Mobility"
            loading="lazy"
            decoding="async"
            data-testid="mm-footer-logo"
            className="h-[76px] w-auto"
            style={{ aspectRatio: "1305 / 855" }}
          />
          <div className="text-[11px] md:text-[11.5px] tracking-[0.18em] uppercase text-sage-dark mt-4">
            {MM_BRAND.tagline}
          </div>
          <p className="text-[15px] leading-relaxed mt-4 text-[#5A564F] max-w-sm">
            A specialized part of {MM_BRAND.name} focused on premium home medical, mobility and
            accessibility equipment.
          </p>
          <a
            href="https://humanhomeliving.com"
            data-testid="mm-footer-visit-hhl"
            className="inline-flex items-center mt-7 px-6 py-3.5 text-[12px] tracking-[0.14em] uppercase border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300"
          >
            Visit Human Home Living
          </a>
        </div>
        <div>
          <div className="eyebrow mb-4">Equipment categories</div>
          {MM_CATEGORIES.map((c) => (
            <FooterLink key={c.slug} to={mmPath(c.slug)} testId={`mm-footer-${c.slug}`}>
              {c.name}
            </FooterLink>
          ))}
        </div>
        <div>
          <div className="eyebrow mb-4">Information</div>
          <FooterLink to={`${MM_ROOT}#about`} testId="mm-footer-about">
            About Medical &amp; Mobility
          </FooterLink>
          <FooterLink to="/contact?ref=medical-mobility" testId="mm-footer-contact">
            Contact
          </FooterLink>
          <FooterLink to="/contact?topic=partnership&ref=medical-mobility" testId="mm-footer-partnership">
            Partnership Enquiries
          </FooterLink>
          <FooterLink to="/affiliate-disclosure?ref=medical-mobility" testId="mm-footer-disclosure-link">
            Affiliate Disclosure
          </FooterLink>
          <FooterLink to="/privacy?ref=medical-mobility" testId="mm-footer-privacy">
            Privacy Policy
          </FooterLink>
          <FooterLink to="/terms?ref=medical-mobility" testId="mm-footer-terms">
            Terms of Use
          </FooterLink>
        </div>
      </div>
    </div>
    <div className="bg-charcoal text-[#BDBAB4]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-7 flex flex-col md:flex-row justify-between gap-4 text-[13px]">
        <p data-testid="mm-footer-disclosure" className="max-w-3xl">
          Information in this section is for general research and planning only and is not medical
          advice. {MM_BRAND.name} may earn commissions through affiliate partnerships when partner
          links appear.
        </p>
        <p>© {new Date().getFullYear()} {MM_BRAND.name}</p>
      </div>
    </div>
  </footer>
);
